import { supabase, CLOUD_RUN_WEBHOOK_URL } from './constants';

export function probVictoire(eloA, eloB, surface = 'Hard') {
  // Déterminer le champ Elo à utiliser en fonction de la surface
  const surfaceKey = surface.toLowerCase() === 'hard' ? 'h' : 
                     surface.toLowerCase() === 'clay' ? 'c' : 
                     surface.toLowerCase() === 'grass' ? 'g' : '';
  
  const eloField = `${surfaceKey}elo`;
  
  // Utiliser le champ Elo spécifique à la surface si disponible
  const eloAValue = eloA[eloField] || eloA.elo || 1500;
  const eloBValue = eloB[eloField] || eloB.elo || 1500;
  
  console.log(`Calcul probVictoire: ${eloField}, ${eloAValue} vs ${eloBValue}`);
  
  return 1 / (1 + 10 ** ((eloBValue - eloAValue) / 400));
}

export function capitalizeFirstLetter(string) {
  if (!string) return '';
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
}

// Enhanced algorithm helper functions
function calculateHeadToHeadFactor(player1, player2) {
  // Placeholder for future head-to-head data integration
  // For now, return neutral factor
  return 0.5;
}

function calculateSurfaceSpecialization(careerData1, careerData2, surface) {
  // Calculate how specialized each player is on this surface
  const winRate1 = parseFloat(careerData1?.['win%']?.replace('%', '') || 50) / 100;
  const winRate2 = parseFloat(careerData2?.['win%']?.replace('%', '') || 50) / 100;
  
  // Get matches played to assess specialization confidence
  const matches1 = careerData1?.m || 0;
  const matches2 = careerData2?.m || 0;
  
  // Weight by experience on surface
  const experienceWeight1 = Math.min(matches1 / 50, 1); // Cap at 50 matches for full weight
  const experienceWeight2 = Math.min(matches2 / 50, 1);
  
  const adjustedWinRate1 = winRate1 * experienceWeight1 + 0.5 * (1 - experienceWeight1);
  const adjustedWinRate2 = winRate2 * experienceWeight2 + 0.5 * (1 - experienceWeight2);
  
  return adjustedWinRate1 / (adjustedWinRate1 + adjustedWinRate2);
}

function calculateMomentumFactor(recentData1, recentData2) {
  // Calculate momentum based on recent performance trends
  const recentWinRate1 = parseFloat(recentData1?.['win%']?.replace('%', '') || 50) / 100;
  const recentWinRate2 = parseFloat(recentData2?.['win%']?.replace('%', '') || 50) / 100;
  
  // Get recent matches count for confidence
  const recentMatches1 = recentData1?.m || 0;
  const recentMatches2 = recentData2?.m || 0;
  
  // Apply confidence weighting based on sample size
  const confidence1 = Math.min(recentMatches1 / 10, 1); // Full confidence at 10+ matches
  const confidence2 = Math.min(recentMatches2 / 10, 1);
  
  const weightedWinRate1 = recentWinRate1 * confidence1 + 0.5 * (1 - confidence1);
  const weightedWinRate2 = recentWinRate2 * confidence2 + 0.5 * (1 - confidence2);
  
  return weightedWinRate1 / (weightedWinRate1 + weightedWinRate2);
}

export function calculateServiceGameWinProb(serverStats, returnerStats) {
  const baseProb = 0.5 * serverStats.holdPercentage + 0.5 * (1 - returnerStats.breakPercentage);
  const firstServeEfficiency = serverStats.firstServeIn * serverStats.firstServeWin;
  const secondServeEfficiency = (1 - serverStats.firstServeIn) * serverStats.secondServeWin;
  const serveEfficiency = firstServeEfficiency + secondServeEfficiency;
  const aceDoubleFaultFactor = (serverStats.aceRate - serverStats.doubleFaultRate) * 0.5;
  const returnFactor = returnerStats.returnPointsWon * 0.7;
  const adjustedProb = baseProb * 0.6 + serveEfficiency * 0.2 + aceDoubleFaultFactor * 0.1 - returnFactor * 0.1;
  return Math.max(0.4, Math.min(0.95, adjustedProb));
}

export function calculateMatchProbability(p1ServiceProb, p2ServiceProb) {
  const p1WinningService = p1ServiceProb;
  const p1WinningReturn = 1 - p2ServiceProb;
  const p1OverallGameStrength = 0.65 * p1WinningService + 0.35 * p1WinningReturn;
  const p2OverallGameStrength = 0.65 * p2ServiceProb + 0.35 * (1 - p1ServiceProb);
  return p1OverallGameStrength / (p1OverallGameStrength + p2OverallGameStrength);
}

export function calculateConfidenceLevel(careerData1, careerData2, recentData1, recentData2, eloProb, statsProb) {
  const matches1 = careerData1?.m || 0;
  const matches2 = careerData2?.m || 0;
  const recentMatches1 = recentData1?.m || 0;
  const recentMatches2 = recentData2?.m || 0;
  const predictionConsistency = 1 - Math.abs(eloProb - statsProb);
  
  let sampleScore = 0;
  if (matches1 > 50 && matches2 > 50) sampleScore = 1;
  else if (matches1 > 30 && matches2 > 30) sampleScore = 0.8;
  else if (matches1 > 15 && matches2 > 15) sampleScore = 0.6;
  else if (matches1 > 5 && matches2 > 5) sampleScore = 0.4;
  else sampleScore = 0.2;
  
  let recentScore = 0;
  if (recentMatches1 > 10 && recentMatches2 > 10) recentScore = 1;
  else if (recentMatches1 > 5 && recentMatches2 > 5) recentScore = 0.7;
  else if (recentMatches1 > 2 && recentMatches2 > 2) recentScore = 0.4;
  else recentScore = 0.2;
  
  const confidenceScore = 0.4 * sampleScore + 0.3 * recentScore + 0.3 * predictionConsistency;
  
  if (confidenceScore > 0.8) return 'high';
  if (confidenceScore > 0.6) return 'medium-high';
  if (confidenceScore > 0.4) return 'medium';
  if (confidenceScore > 0.2) return 'medium-low';
  return 'low';
}

export async function calculateAdvancedProbability(player1, player2, surface) {
  const surfaceKey = surface.toLowerCase() === 'hard' ? 'h' : 
                     surface.toLowerCase() === 'clay' ? 'c' : 
                     surface.toLowerCase() === 'grass' ? 'g' : '';
  
  if (!surfaceKey) return { prob1: 0.5, prob2: 0.5, confidence: 'low' };
  
  const eloField = `${surfaceKey}elo`;
  
  // Utiliser la fonction probVictoire pour calculer la probabilité de base
  const baseProb1 = probVictoire(player1, player2, surface);
  const baseProb2 = 1 - baseProb1;
  
  const { data: careerData1, error: careerError1 } = await supabase
    .from('career_splits')
    .select('*')
    .eq('player_slug', player1.profile_url)
    .eq('split', capitalizeFirstLetter(surface))
    .single();
    
  const { data: careerData2, error: careerError2 } = await supabase
    .from('career_splits')
    .select('*')
    .eq('player_slug', player2.profile_url)
    .eq('split', capitalizeFirstLetter(surface))
    .single();
    
  // Utiliser la nouvelle méthode pour la forme récente basée sur recent_results
  const enhancedMomentum = await calculateEnhancedMomentumFactor(player1, player2, surface);
  const recentFormFromResults = enhancedMomentum.details;
  
  // Garder l'ancienne méthode comme fallback
  const { data: recentData1, error: recentError1 } = await supabase
    .from('last52_splits')
    .select('*')
    .eq('player_slug', player1.profile_url)
    .eq('split', capitalizeFirstLetter(surface))
    .single();
    
  const { data: recentData2, error: recentError2 } = await supabase
    .from('last52_splits')
    .select('*')
    .eq('player_slug', player2.profile_url)
    .eq('split', capitalizeFirstLetter(surface))
    .single();
    
  if (careerError1 || careerError2) {
    return { 
      prob1: baseProb1, 
      prob2: baseProb2, 
      confidence: 'low',
      factors: {
        elo: { player1: player1[eloField], player2: player2[eloField] },
        recentFormFromResults: recentFormFromResults
      }
    };
  }
  
  const careerWinRate1 = parseFloat(careerData1?.['win%']?.replace('%', '') || 50) / 100;
  const careerWinRate2 = parseFloat(careerData2?.['win%']?.replace('%', '') || 50) / 100;
  
  // Utiliser les nouvelles données de forme récente si disponibles, sinon fallback
  const recentWinRate1 = recentFormFromResults.player1.confidence > 0 ? 
    recentFormFromResults.player1.winRate : 
    parseFloat(recentData1?.['win%']?.replace('%', '') || 50) / 100;
  const recentWinRate2 = recentFormFromResults.player2.confidence > 0 ? 
    recentFormFromResults.player2.winRate : 
    parseFloat(recentData2?.['win%']?.replace('%', '') || 50) / 100;
  
  const service1 = {
    holdPercentage: parseFloat(careerData1?.['hld%']?.replace('%', '') || 80) / 100,
    firstServeIn: parseFloat(careerData1?.['1stin']?.replace('%', '') || 60) / 100,
    firstServeWin: parseFloat(careerData1?.['1st%']?.replace('%', '') || 70) / 100,
    secondServeWin: parseFloat(careerData1?.['2nd%']?.replace('%', '') || 50) / 100,
    aceRate: parseFloat(careerData1?.['a%']?.replace('%', '') || 5) / 100,
    doubleFaultRate: parseFloat(careerData1?.['df%']?.replace('%', '') || 3) / 100
  };
  
  const service2 = {
    holdPercentage: parseFloat(careerData2?.['hld%']?.replace('%', '') || 80) / 100,
    firstServeIn: parseFloat(careerData2?.['1stin']?.replace('%', '') || 60) / 100,
    firstServeWin: parseFloat(careerData2?.['1st%']?.replace('%', '') || 70) / 100,
    secondServeWin: parseFloat(careerData2?.['2nd%']?.replace('%', '') || 50) / 100,
    aceRate: parseFloat(careerData2?.['a%']?.replace('%', '') || 5) / 100,
    doubleFaultRate: parseFloat(careerData2?.['df%']?.replace('%', '') || 3) / 100
  };
  
  const return1 = {
    breakPercentage: parseFloat(careerData1?.['brk%']?.replace('%', '') || 20) / 100,
    returnPointsWon: parseFloat(careerData1?.['rpw']?.replace('%', '') || 40) / 100
  };
  
  const return2 = {
    breakPercentage: parseFloat(careerData2?.['brk%']?.replace('%', '') || 20) / 100,
    returnPointsWon: parseFloat(careerData2?.['rpw']?.replace('%', '') || 40) / 100
  };
  
  const totalPointsWon1 = parseFloat(careerData1?.['tpw']?.replace('%', '') || 50) / 100;
  const totalPointsWon2 = parseFloat(careerData2?.['tpw']?.replace('%', '') || 50) / 100;
  
  const serviceGameProb1 = calculateServiceGameWinProb(service1, return2);
  const serviceGameProb2 = calculateServiceGameWinProb(service2, return1);
  const matchProbFromStats1 = calculateMatchProbability(serviceGameProb1, serviceGameProb2);
  const matchProbFromStats2 = 1 - matchProbFromStats1;
  // Calculer la forme récente avec pondération améliorée
  const recentFormConfidence1 = recentFormFromResults.player1.confidence;
  const recentFormConfidence2 = recentFormFromResults.player2.confidence;
  
  // Pondérer la forme récente en fonction de la confiance (nombre de matchs disponibles)
  const recentFormFactor1 = (0.7 * recentWinRate1 * recentFormConfidence1) + 
                           (0.3 * careerWinRate1) + 
                           (0.3 * recentWinRate1 * (1 - recentFormConfidence1));
  const recentFormFactor2 = (0.7 * recentWinRate2 * recentFormConfidence2) + 
                           (0.3 * careerWinRate2) + 
                           (0.3 * recentWinRate2 * (1 - recentFormConfidence2));
  const recentFormRatio = recentFormFactor1 / (recentFormFactor1 + recentFormFactor2);
  
  // Enhanced algorithm with additional factors
  const headToHeadFactor = calculateHeadToHeadFactor(player1, player2); // Placeholder for future H2H data
  const surfaceSpecializationFactor = calculateSurfaceSpecialization(careerData1, careerData2, surface);
  
  // Utiliser le nouveau facteur de momentum amélioré
  const momentumFactor = enhancedMomentum.factor;
  const legacyMomentumFactor = calculateMomentumFactor(recentData1, recentData2);
  
  // Weighted combination with enhanced factors - v2.2: Amplifier les écarts significatifs
  const rawFinalProb1 = 0.35 * baseProb1 + 
                        0.25 * matchProbFromStats1 + 
                        0.2 * recentFormRatio + 
                        0.1 * surfaceSpecializationFactor + 
                        0.1 * momentumFactor;
  
  // Amplification des écarts significatifs (v2.2)
  // Si l'écart est important, l'amplifier pour refléter la réalité
  const rawDifference = Math.abs(rawFinalProb1 - 0.5);
  let amplificationFactor = 1.0;
  
  // Amplifier progressivement selon l'écart initial
  if (rawDifference > 0.25) {        // Écart > 25% -> amplifier fortement
    amplificationFactor = 1.4;
  } else if (rawDifference > 0.15) { // Écart > 15% -> amplifier modérément
    amplificationFactor = 1.25;
  } else if (rawDifference > 0.08) { // Écart > 8% -> amplifier légèrement
    amplificationFactor = 1.1;
  }
  
  // Appliquer l'amplification en gardant les bornes [0.05, 0.95]
  const amplifiedDifference = Math.min(rawDifference * amplificationFactor, 0.45);
  const finalProb1 = rawFinalProb1 > 0.5 ? 
    Math.min(0.5 + amplifiedDifference, 0.95) : 
    Math.max(0.5 - amplifiedDifference, 0.05);
  const finalProb2 = 1 - finalProb1;
  
  // Calculer la confiance en tenant compte de la qualité des données récentes
  const recentDataQuality = (recentFormConfidence1 + recentFormConfidence2) / 2;
  const baseConfidenceLevel = calculateConfidenceLevel(
    careerData1, careerData2, recentData1, recentData2, baseProb1, matchProbFromStats1
  );
  
  // Ajuster la confiance en fonction de la qualité des données récentes
  let confidenceLevel = baseConfidenceLevel;
  if (recentDataQuality > 0.8) {
    confidenceLevel = baseConfidenceLevel === 'low' ? 'medium-low' : 
                     baseConfidenceLevel === 'medium-low' ? 'medium' :
                     baseConfidenceLevel === 'medium' ? 'medium-high' :
                     baseConfidenceLevel === 'medium-high' ? 'high' : 'high';
  } else if (recentDataQuality < 0.3) {
    confidenceLevel = baseConfidenceLevel === 'high' ? 'medium-high' : 
                     baseConfidenceLevel === 'medium-high' ? 'medium' :
                     baseConfidenceLevel === 'medium' ? 'medium-low' :
                     baseConfidenceLevel === 'medium-low' ? 'low' : 'low';
  }
  
  return {
    prob1: finalProb1,
    prob2: finalProb2,
    confidence: confidenceLevel,
    factors: {
      elo: { player1: player1[eloField], player2: player2[eloField] },
      baseProb: { player1: baseProb1, player2: baseProb2 },
      serviceGameProb: { player1: serviceGameProb1, player2: serviceGameProb2 },
      matchStatProb: { player1: matchProbFromStats1, player2: matchProbFromStats2 },
      recentForm: { player1: recentWinRate1, player2: recentWinRate2 },
      careerForm: { player1: careerWinRate1, player2: careerWinRate2 },
      totalPointsWon: { player1: totalPointsWon1, player2: totalPointsWon2 },
      serviceStats: { player1: service1, player2: service2 },
      returnStats: { player1: return1, player2: return2 },
      // Enhanced factors
      surfaceSpecialization: surfaceSpecializationFactor,
      momentum: momentumFactor,
      headToHead: headToHeadFactor,
      // Nouvelles données de forme récente détaillées
      recentFormFromResults: {
        player1: {
          winRate: recentFormFromResults.player1.winRate,
          confidence: recentFormFromResults.player1.confidence,
          matchCount: recentFormFromResults.player1.matchCount,
          formString: recentFormFromResults.player1.formString,
          surfaceMatchCount: recentFormFromResults.player1.surfaceMatchCount
        },
        player2: {
          winRate: recentFormFromResults.player2.winRate,
          confidence: recentFormFromResults.player2.confidence,
          matchCount: recentFormFromResults.player2.matchCount,
          formString: recentFormFromResults.player2.formString,
          surfaceMatchCount: recentFormFromResults.player2.surfaceMatchCount
        }
      },
      recentDataQuality: recentDataQuality,
      legacyMomentum: legacyMomentumFactor,
      // Nouvelles données d'amplification v2.2
      amplification: {
        rawProb1: rawFinalProb1,
        rawDifference: rawDifference,
        amplificationFactor: amplificationFactor,
        amplifiedDifference: amplifiedDifference
      }
    }
  };
}

export async function getPlayerComparison(player1, player2, surface) {
  // Calculer d'abord les probabilités de base avec l'Elo spécifique à la surface
  const eloProb1 = probVictoire(player1, player2, surface);
  const eloProb2 = 1 - eloProb1;
  
  // Ensuite, calculer les probabilités avancées
  const advancedProb = await calculateAdvancedProbability(player1, player2, surface);
  
  return {
    surface,
    simpleElo: {
      player1: Math.round(eloProb1 * 100),
      player2: Math.round(eloProb2 * 100)
    },
    advanced: {
      player1: Math.round(advancedProb.prob1 * 100),
      player2: Math.round(advancedProb.prob2 * 100),
      confidence: advancedProb.confidence,
      factors: advancedProb.factors
    }
  };
}

export function calculateSetScenarios(player1Win, isGrandSlam) {
  const player2Win = 1 - player1Win;
  const p1 = player1Win;
  const p2 = player2Win;
  const setDependencyFactor = 0.9;
  
  if (!isGrandSlam) {
    const win2_0 = p1 * (p1 * setDependencyFactor + (1-setDependencyFactor) * p1);
    const win2_1 = p1 * p2 * p1;
    const lose0_2 = p2 * (p2 * setDependencyFactor + (1-setDependencyFactor) * p2);
    const lose1_2 = p2 * p1 * p2;
    return [
      { scenario: "2-0", probability: win2_0 * 100, winner: 1 },
      { scenario: "2-1", probability: win2_1 * 100, winner: 1 },
      { scenario: "0-2", probability: lose0_2 * 100, winner: 2 },
      { scenario: "1-2", probability: lose1_2 * 100, winner: 2 }
    ];
  } else {
    const win3_0 = p1 * p1 * p1;
    const win3_1 = 3 * p1 * p1 * p1 * p2;
    const win3_2 = 6 * p1 * p1 * p1 * p2 * p2;
    const lose0_3 = p2 * p2 * p2;
    const lose1_3 = 3 * p2 * p2 * p2 * p1;
    const lose2_3 = 6 * p2 * p2 * p2 * p1 * p1;
    const totalP = win3_0 + win3_1 + win3_2 + lose0_3 + lose1_3 + lose2_3;
    return [
      { scenario: "3-0", probability: (win3_0 / totalP) * 100, winner: 1 },
      { scenario: "3-1", probability: (win3_1 / totalP) * 100, winner: 1 },
      { scenario: "3-2", probability: (win3_2 / totalP) * 100, winner: 1 },
      { scenario: "0-3", probability: (lose0_3 / totalP) * 100, winner: 2 },
      { scenario: "1-3", probability: (lose1_3 / totalP) * 100, winner: 2 },
      { scenario: "2-3", probability: (lose2_3 / totalP) * 100, winner: 2 }
    ];
  }
}

export async function fetchTennisAbstractStats(profileName) {
  if (!profileName) return null;
  try {
    const res = await fetch(CLOUD_RUN_WEBHOOK_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ joueurs: [profileName] })
    });
    const text = await res.text();
    if (!res.ok) {
      throw new Error(`Erreur API: ${res.status} ${res.statusText} - ${text}`);
    }
    return { message: text };
  } catch (error) {
    console.error("Erreur lors de l'appel à l'API Python Cloud Run:", error);
    return { message: error.message };
  }
}

// Function to fetch additional player statistics from database
async function fetchAdditionalPlayerStats(player1Slug, player2Slug, surface) {
  try {
    const stats = {
      player1: { slug: player1Slug },
      player2: { slug: player2Slug }
    };

    // Fetch career splits for both players
    const { data: careerSplits } = await supabase
      .from('career_splits')
      .select('*')
      .in('player_slug', [player1Slug, player2Slug]);

    // Fetch last 52 weeks splits
    const { data: last52Splits } = await supabase
      .from('last52_splits')
      .select('*')
      .in('player_slug', [player1Slug, player2Slug]);

    // Fetch recent results
    const { data: recentResults } = await supabase
      .from('recent_results')
      .select('*')
      .in('player_slug', [player1Slug, player2Slug])
      .order('date', { ascending: false })
      .limit(20);

    // Fetch head to head data
    const { data: h2hData } = await supabase
      .from('head_to_head')
      .select('*')
      .in('player_slug', [player1Slug, player2Slug]);

    // Fetch point-by-point stats
    const { data: pbpPoints } = await supabase
      .from('pbp_points')
      .select('*')
      .in('player_slug', [player1Slug, player2Slug]);

    // Fetch winners/errors stats
    const { data: winnersErrors } = await supabase
      .from('winners_errors')
      .select('*')
      .in('player_slug', [player1Slug, player2Slug]);

    // Process and organize the data
    [player1Slug, player2Slug].forEach((slug, index) => {
      const playerKey = `player${index + 1}`;
      
      // Career splits by surface
      const playerCareerSplits = careerSplits?.filter(s => s.player_slug === slug) || [];
      const surfaceCareerSplit = playerCareerSplits.find(s => s.split?.toLowerCase().includes(surface.toLowerCase()));
      
      // Last 52 weeks splits
      const playerLast52 = last52Splits?.filter(s => s.player_slug === slug) || [];
      const surfaceLast52 = playerLast52.find(s => s.split?.toLowerCase().includes(surface.toLowerCase()));
      
      // Recent results on this surface
      const playerRecentResults = recentResults?.filter(r => 
        r.player_slug === slug && 
        r.surface?.toLowerCase() === surface.toLowerCase()
      ) || [];

      // H2H against opponent
      const opponentSlug = index === 0 ? player2Slug : player1Slug;
      const h2h = h2hData?.find(h => 
        h.player_slug === slug && 
        h.opponent?.toLowerCase().includes(opponentSlug.replace('-', ' '))
      );

      // Point-by-point stats
      const playerPbp = pbpPoints?.filter(p => p.player_slug === slug) || [];
      
      // Winners/errors stats
      const playerWE = winnersErrors?.filter(w => w.player_slug === slug) || [];

      stats[playerKey] = {
        ...stats[playerKey],
        careerOnSurface: surfaceCareerSplit,
        last52OnSurface: surfaceLast52,
        recentResultsOnSurface: playerRecentResults.slice(0, 5), // Last 5 matches on surface
        headToHead: h2h,
        pointByPointStats: playerPbp[0], // Most recent PBP stats
        winnersErrorsStats: playerWE[0] // Most recent W/E stats
      };
    });

    return stats;
  } catch (error) {
    console.error('Error fetching additional player stats:', error);
    return { player1: {}, player2: {} };
  }
}

export async function generateMatchAnalysisSummary(player1, player2, surface, comparisonResult) {
  try {
    // Fetch additional database statistics for both players
    const additionalStats = await fetchAdditionalPlayerStats(player1.player_slug, player2.player_slug, surface);
    
    // Prepare the analysis data for the LLM
    const factors = comparisonResult.advanced.factors;
    const winner = comparisonResult.advanced.player1 > comparisonResult.advanced.player2 ? player1.player : player2.player;
    const winnerProb = Math.max(comparisonResult.advanced.player1, comparisonResult.advanced.player2);
    const confidence = comparisonResult.advanced.confidence;
    
    // Create a comprehensive prompt with all available data
    const prompt = `Analyze this tennis match prediction and provide a brief, insightful summary in French.

MATCH: ${player1.player} vs ${player2.player}
SURFACE: ${surface}
PREDICTED WINNER: ${winner} (${winnerProb}%)
CONFIDENCE: ${confidence}

CORE STATISTICS:
- ELO Ratings: ${factors.elo.player1} vs ${factors.elo.player2}
- Service Game Win Probability: ${(factors.serviceGameProb.player1 * 100).toFixed(1)}% vs ${(factors.serviceGameProb.player2 * 100).toFixed(1)}%
- Recent Form (Win %): ${(factors.recentForm.player1 * 100).toFixed(1)}% vs ${(factors.recentForm.player2 * 100).toFixed(1)}%
- Career Form on ${surface}: ${(factors.careerForm.player1 * 100).toFixed(1)}% vs ${(factors.careerForm.player2 * 100).toFixed(1)}%

SERVICE STATS:
${player1.player}: Hold ${(factors.serviceStats.player1.holdPercentage * 100).toFixed(1)}%, 1st Serve Win ${(factors.serviceStats.player1.firstServeWin * 100).toFixed(1)}%, 2nd Serve Win ${(factors.serviceStats.player1.secondServeWin * 100).toFixed(1)}%, Aces ${(factors.serviceStats.player1.aceRate * 100).toFixed(1)}%
${player2.player}: Hold ${(factors.serviceStats.player2.holdPercentage * 100).toFixed(1)}%, 1st Serve Win ${(factors.serviceStats.player2.firstServeWin * 100).toFixed(1)}%, 2nd Serve Win ${(factors.serviceStats.player2.secondServeWin * 100).toFixed(1)}%, Aces ${(factors.serviceStats.player2.aceRate * 100).toFixed(1)}%

RETURN STATS:
${player1.player}: Break ${(factors.returnStats.player1.breakPercentage * 100).toFixed(1)}%, Return Points Won ${(factors.returnStats.player1.returnPointsWon * 100).toFixed(1)}%
${player2.player}: Break ${(factors.returnStats.player2.breakPercentage * 100).toFixed(1)}%, Return Points Won ${(factors.returnStats.player2.returnPointsWon * 100).toFixed(1)}%

DETAILED SURFACE STATISTICS:
${additionalStats.player1.careerOnSurface ? `${player1.player} Career on ${surface}: ${additionalStats.player1.careerOnSurface.w || 0}W-${additionalStats.player1.careerOnSurface.l || 0}L (${additionalStats.player1.careerOnSurface['win%'] || 'N/A'}), Hold ${additionalStats.player1.careerOnSurface['hld%'] || 'N/A'}, Break ${additionalStats.player1.careerOnSurface['brk%'] || 'N/A'}` : `${player1.player}: No detailed surface data`}
${additionalStats.player2.careerOnSurface ? `${player2.player} Career on ${surface}: ${additionalStats.player2.careerOnSurface.w || 0}W-${additionalStats.player2.careerOnSurface.l || 0}L (${additionalStats.player2.careerOnSurface['win%'] || 'N/A'}), Hold ${additionalStats.player2.careerOnSurface['hld%'] || 'N/A'}, Break ${additionalStats.player2.careerOnSurface['brk%'] || 'N/A'}` : `${player2.player}: No detailed surface data`}

RECENT FORM ON ${surface.toUpperCase()}:
${additionalStats.player1.last52OnSurface ? `${player1.player} Last 52 weeks: ${additionalStats.player1.last52OnSurface.w || 0}W-${additionalStats.player1.last52OnSurface.l || 0}L (${additionalStats.player1.last52OnSurface['win%'] || 'N/A'})` : `${player1.player}: No recent surface data`}
${additionalStats.player2.last52OnSurface ? `${player2.player} Last 52 weeks: ${additionalStats.player2.last52OnSurface.w || 0}W-${additionalStats.player2.last52OnSurface.l || 0}L (${additionalStats.player2.last52OnSurface['win%'] || 'N/A'})` : `${player2.player}: No recent surface data`}

ENHANCED RECENT FORM ANALYSIS (Algorithm v2.1):
${player1.player} - 10 derniers matchs: ${factors.recentFormFromResults?.player1?.formString || 'N/A'} (${Math.round((factors.recentFormFromResults?.player1?.winRate || 0) * 100)}% sur ${factors.recentFormFromResults?.player1?.matchCount || 0} matchs)
${player1.player} - Momentum immédiat (3 matchs): ${factors.recentFormFromResults?.player1?.ultraRecentForm || 'N/A'} (${Math.round((factors.recentFormFromResults?.player1?.ultraRecentWinRate || 0) * 100)}%)
${player1.player} - Matchs sur ${surface}: ${factors.recentFormFromResults?.player1?.surfaceMatchCount || 0} matchs récents
${player1.player} - Confiance des données: ${Math.round((factors.recentFormFromResults?.player1?.confidence || 0) * 100)}%

${player2.player} - 10 derniers matchs: ${factors.recentFormFromResults?.player2?.formString || 'N/A'} (${Math.round((factors.recentFormFromResults?.player2?.winRate || 0) * 100)}% sur ${factors.recentFormFromResults?.player2?.matchCount || 0} matchs)
${player2.player} - Momentum immédiat (3 matchs): ${factors.recentFormFromResults?.player2?.ultraRecentForm || 'N/A'} (${Math.round((factors.recentFormFromResults?.player2?.ultraRecentWinRate || 0) * 100)}%)
${player2.player} - Matchs sur ${surface}: ${factors.recentFormFromResults?.player2?.surfaceMatchCount || 0} matchs récents
${player2.player} - Confiance des données: ${Math.round((factors.recentFormFromResults?.player2?.confidence || 0) * 100)}%

MOMENTUM ANALYSIS:
Facteur de momentum combiné: ${Math.round((factors.momentum || 0.5) * 100)}% en faveur de ${factors.momentum > 0.5 ? player1.player : player2.player}
Qualité globale des données récentes: ${Math.round((factors.recentDataQuality || 0) * 100)}%

HEAD-TO-HEAD:
${additionalStats.player1.headToHead ? `${player1.player} vs opponent: ${additionalStats.player1.headToHead.w || 0}W-${additionalStats.player1.headToHead.l || 0}L (${additionalStats.player1.headToHead['win%'] || 'N/A'})` : 'No H2H data available'}

PLAYING STYLE INDICATORS:
${additionalStats.player1.winnersErrorsStats ? `${player1.player}: Winners/UE ratio ${additionalStats.player1.winnersErrorsStats.ratio || 'N/A'}, Rally winners ${additionalStats.player1.winnersErrorsStats.rallywinners || 'N/A'}` : ''}
${additionalStats.player2.winnersErrorsStats ? `${player2.player}: Winners/UE ratio ${additionalStats.player2.winnersErrorsStats.ratio || 'N/A'}, Rally winners ${additionalStats.player2.winnersErrorsStats.rallywinners || 'N/A'}` : ''}

RECENT MATCHES ON ${surface.toUpperCase()}:
${additionalStats.player1.recentResultsOnSurface?.length > 0 ? `${player1.player}: ${additionalStats.player1.recentResultsOnSurface.map(r => `${r.tournament} ${r.rd} (${r.score})`).join(', ').substring(0, 100)}...` : `${player1.player}: No recent matches on ${surface}`}
${additionalStats.player2.recentResultsOnSurface?.length > 0 ? `${player2.player}: ${additionalStats.player2.recentResultsOnSurface.map(r => `${r.tournament} ${r.rd} (${r.score})`).join(', ').substring(0, 100)}...` : `${player2.player}: No recent matches on ${surface}`}

Please provide a concise analysis (max 180 words) in French that:
1. Explains why ${winner} is favored based on the most compelling statistics
2. Highlights the key advantages (surface specialization, recent form, playing style, H2H)
3. **PRIORITIZE the new enhanced recent form data (10 matchs + momentum immédiat)** - this is the most accurate and up-to-date information
4. Analyze the contrast between 10-match form vs immediate momentum (3 matches) - this can reveal important trends
5. Consider data quality and confidence levels in your assessment
6. Mentions any potential concerns or underdog factors
7. Establishes a confidence percentage (0-100%) for this prediction based on data quality and statistical consistency

Format your response as:
ANALYSIS: [Your analysis in French]
CONFIDENCE: [Number between 0-100]

**IMPORTANT**: Give special attention to the Enhanced Recent Form Analysis (Algorithm v2.1) data as it represents real match-by-match results rather than aggregated statistics. Look for patterns like:
- Divergence between 10-match form and 3-match momentum
- Surface-specific match counts and their relevance
- Data confidence levels and their impact on prediction reliability
- Recent winning/losing streaks that might indicate current player state

Focus on the most significant and unique insights from this comprehensive data.`;

    // Check if API key is available
    const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
    if (!apiKey) {
      console.log('OpenAI API key not found, using fallback analysis');
      throw new Error('OpenAI API key not configured');
    }

    // Call ChatGPT API
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`
      },
      body: JSON.stringify({
        model: 'gpt-4.1-nano',
        messages: [
          {
            role: 'system',
            content: 'Tu es un expert en analyse de tennis qui fournit des analyses concises et perspicaces des matchs basées sur les statistiques. Réponds toujours en français.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        max_tokens: 400,
        temperature: 0.7
      })
    });

    if (!response.ok) {
      throw new Error(`OpenAI API error: ${response.status}`);
    }

    const data = await response.json();
    const content = data.choices[0].message.content;
    
    // Parse the response to extract analysis and confidence
    const analysisMatch = content.match(/ANALYSIS:\s*(.*?)(?=CONFIDENCE:|$)/s);
    const confidenceMatch = content.match(/CONFIDENCE:\s*(\d+)/);
    
    const analysis = analysisMatch ? analysisMatch[1].trim() : content;
    const aiConfidence = confidenceMatch ? parseInt(confidenceMatch[1]) : null;
    
    return {
      summary: analysis,
      confidence: aiConfidence,
      generated: true,
      timestamp: new Date().toISOString()
    };

  } catch (error) {
    console.error('Error generating AI summary:', error);
    
    // Fallback to rule-based summary if AI fails
    return generateFallbackSummary(player1, player2, surface, comparisonResult);
  }
}

function generateFallbackSummary(player1, player2, surface, comparisonResult) {
  const factors = comparisonResult.advanced.factors;
  const winner = comparisonResult.advanced.player1 > comparisonResult.advanced.player2 ? player1.player : player2.player;
  const winnerProb = Math.max(comparisonResult.advanced.player1, comparisonResult.advanced.player2);
  
  const winnerFactors = winner === player1.player ? factors.serviceStats.player1 : factors.serviceStats.player2;
  const loserFactors = winner === player1.player ? factors.serviceStats.player2 : factors.serviceStats.player1;
  
  let summary = `${winner} est favori avec ${winnerProb}% de chances de victoire sur ${surface.toLowerCase()}. `;
  
  // Analyze enhanced recent form data (v2.1)
  if (factors.recentFormFromResults) {
    const winnerRecentForm = winner === player1.player ? factors.recentFormFromResults.player1 : factors.recentFormFromResults.player2;
    const loserRecentForm = winner === player1.player ? factors.recentFormFromResults.player2 : factors.recentFormFromResults.player1;
    
    if (winnerRecentForm.winRate > loserRecentForm.winRate + 0.1) {
      summary += `Sa forme récente est excellente (${winnerRecentForm.formString || 'N/A'} - ${Math.round(winnerRecentForm.winRate * 100)}% sur 10 matchs). `;
    }
    
    // Analyze momentum divergence
    if (winnerRecentForm.ultraRecentWinRate && loserRecentForm.ultraRecentWinRate) {
      const momentumDiff = winnerRecentForm.ultraRecentWinRate - loserRecentForm.ultraRecentWinRate;
      if (Math.abs(momentumDiff) > 0.3) {
        if (momentumDiff > 0) {
          summary += `Son momentum immédiat est très positif (${winnerRecentForm.ultraRecentForm}). `;
        } else {
          summary += `Attention: son momentum récent est moins bon (${winnerRecentForm.ultraRecentForm}) malgré sa forme générale. `;
        }
      }
    }
  }
  
  // Analyze key advantages
  if (winnerFactors.holdPercentage > loserFactors.holdPercentage + 0.05) {
    summary += `Son service est plus solide (${(winnerFactors.holdPercentage * 100).toFixed(1)}% de jeux de service gagnés). `;
  }
  
  if (factors.recentForm[winner === player1.player ? 'player1' : 'player2'] > 
      factors.recentForm[winner === player1.player ? 'player2' : 'player1'] + 0.1) {
    summary += `Sa forme récente traditionnelle est également supérieure. `;
  }
  
  // Add confidence assessment and calculate numerical confidence
  const confidenceLevel = comparisonResult.advanced.confidence;
  let numericalConfidence = 50; // Default confidence
  
  if (confidenceLevel === 'high') {
    summary += `Prédiction très fiable basée sur des données complètes.`;
    numericalConfidence = 85;
  } else if (confidenceLevel === 'medium-high') {
    summary += `Prédiction fiable avec de bonnes données statistiques.`;
    numericalConfidence = 75;
  } else if (confidenceLevel === 'medium') {
    summary += `Prédiction modérée avec des données partielles.`;
    numericalConfidence = 65;
  } else if (confidenceLevel === 'medium-low') {
    summary += `Prédiction incertaine, données limitées.`;
    numericalConfidence = 55;
  } else {
    summary += `Prédiction à prendre avec précaution, données limitées.`;
    numericalConfidence = 45;
  }
  
  return {
    summary,
    confidence: numericalConfidence,
    generated: false,
    fallback: true,
    timestamp: new Date().toISOString()
  };
}

// Enhanced version of getPlayerComparison that includes AI summary
export async function getPlayerComparisonWithSummary(player1, player2, surface, includeAISummary = false) {
  const comparison = await getPlayerComparison(player1, player2, surface);
  
  if (includeAISummary && comparison.advanced.factors) {
    try {
      const aiSummary = await generateMatchAnalysisSummary(player1, player2, surface, comparison);
      comparison.aiSummary = aiSummary;
    } catch (error) {
      console.error('Failed to generate AI summary:', error);
    }
  }
  
  return comparison;
}

// Nouvelle fonction pour calculer la forme récente basée sur recent_results
async function calculateRecentFormFromResults(player1, player2, surface, numberOfMatches = 10) {
  try {
    // Récupérer les résultats récents pour les deux joueurs
    const { data: recentResults1, error: error1 } = await supabase
      .from('recent_results')
      .select('result, date, surface')
      .eq('player_slug', player1.profile_url)
      .not('result', 'is', null)
      .order('date', { ascending: false })
      .limit(numberOfMatches);

    const { data: recentResults2, error: error2 } = await supabase
      .from('recent_results')
      .select('result, date, surface')
      .eq('player_slug', player2.profile_url)
      .not('result', 'is', null)
      .order('date', { ascending: false })
      .limit(numberOfMatches);

    if (error1 || error2) {
      console.log('Erreur lors de la récupération des résultats récents, utilisation des données par défaut');
      return {
        player1: { winRate: 0.5, confidence: 0, matchCount: 0, formString: 'N/A' },
        player2: { winRate: 0.5, confidence: 0, matchCount: 0, formString: 'N/A' }
      };
    }

    // Fonction pour calculer les stats d'un joueur
    const calculatePlayerForm = (results, targetSurface) => {
      if (!results || results.length === 0) {
        return { winRate: 0.5, confidence: 0, matchCount: 0, formString: 'N/A' };
      }

      // Filtrer par surface si spécifiée
      let filteredResults = results;
      if (targetSurface && targetSurface.toLowerCase() !== 'all') {
        filteredResults = results.filter(r => 
          r.surface && r.surface.toLowerCase() === targetSurface.toLowerCase()
        );
      }

      // Si pas assez de résultats sur cette surface, utiliser tous les résultats
      if (filteredResults.length < 2) {
        filteredResults = results;
      }

      const wins = filteredResults.filter(r => r.result === 'win').length;
      const total = filteredResults.length;
      const winRate = total > 0 ? wins / total : 0.5;
      
      // Calculer la confiance basée sur le nombre de matchs
      const confidence = Math.min(total / numberOfMatches, 1);
      
      // Créer une chaîne de forme (W/L pour les derniers matchs)
      const formString = filteredResults
        .slice(0, 10)
        .map(r => r.result === 'win' ? 'W' : 'L')
        .join('');

      return {
        winRate,
        confidence,
        matchCount: total,
        formString,
        surfaceMatchCount: filteredResults.length
      };
    };

    const form1 = calculatePlayerForm(recentResults1, surface);
    const form2 = calculatePlayerForm(recentResults2, surface);

    return {
      player1: form1,
      player2: form2
    };

  } catch (error) {
    console.error('Erreur lors du calcul de la forme récente:', error);
    return {
      player1: { winRate: 0.5, confidence: 0, matchCount: 0, formString: 'N/A' },
      player2: { winRate: 0.5, confidence: 0, matchCount: 0, formString: 'N/A' }
    };
  }
}

// Fonction améliorée pour calculer le momentum avec les vraies données récentes
async function calculateEnhancedMomentumFactor(player1, player2, surface) {
  const recentForm = await calculateRecentFormFromResults(player1, player2, surface, 10);
  const ultraRecentForm = await calculateRecentFormFromResults(player1, player2, surface, 3);
  
  const form1 = recentForm.player1;
  const form2 = recentForm.player2;
  const ultraForm1 = ultraRecentForm.player1;
  const ultraForm2 = ultraRecentForm.player2;
  
  // Appliquer un poids basé sur la confiance (nombre de matchs disponibles)
  const weightedWinRate1 = form1.winRate * form1.confidence + 0.5 * (1 - form1.confidence);
  const weightedWinRate2 = form2.winRate * form2.confidence + 0.5 * (1 - form2.confidence);
  
  // Ajouter un facteur de momentum immédiat basé sur les 3 derniers matchs
  const immediateMomentum1 = ultraForm1.confidence > 0 ? ultraForm1.winRate : 0.5;
  const immediateMomentum2 = ultraForm2.confidence > 0 ? ultraForm2.winRate : 0.5;
  
  // Combiner forme récente (70%) et momentum immédiat (30%)
  const combinedMomentum1 = 0.7 * weightedWinRate1 + 0.3 * immediateMomentum1;
  const combinedMomentum2 = 0.7 * weightedWinRate2 + 0.3 * immediateMomentum2;
  
  // Calculer le facteur de momentum final
  const momentumFactor = combinedMomentum1 / (combinedMomentum1 + combinedMomentum2);
  
  return {
    factor: momentumFactor,
    details: {
      player1: {
        ...form1,
        ultraRecentForm: ultraForm1.formString,
        ultraRecentWinRate: ultraForm1.winRate,
        ultraRecentConfidence: ultraForm1.confidence
      },
      player2: {
        ...form2,
        ultraRecentForm: ultraForm2.formString,
        ultraRecentWinRate: ultraForm2.winRate,
        ultraRecentConfidence: ultraForm2.confidence
      },
      weightedWinRate1,
      weightedWinRate2,
      immediateMomentum1,
      immediateMomentum2,
      combinedMomentum1,
      combinedMomentum2
    }
  };
}

// Fonction pour obtenir un résumé des améliorations de l'algorithme
export function getAlgorithmImprovements() {
  return {
    version: "2.2 - Amplification des écarts significatifs",
    improvements: [
      {
        title: "Amplification des écarts significatifs",
        description: "Amplification progressive des écarts importants pour refléter les vraies différences de niveau",
        impact: "Prédictions plus réalistes pour les matchs déséquilibrés (ex: Top 10 vs joueur classé 100+)"
      },
      {
        title: "Fenêtre temporelle optimisée",
        description: "Passage de 5 à 10 matchs pour la forme récente - plus représentatif (2-3 mois vs 1 tournoi)",
        impact: "Meilleur équilibre entre réactivité et stabilité statistique"
      },
      {
        title: "Vraie forme récente",
        description: "Utilise maintenant les vrais résultats de matchs de la table recent_results au lieu des statistiques agrégées",
        impact: "Plus précis et réactif aux changements de forme"
      },
      {
        title: "Pondération adaptative",
        description: "Ajuste automatiquement l'importance de la forme récente selon la qualité des données disponibles",
        impact: "Meilleure fiabilité des prédictions"
      },
      {
        title: "Confiance des données",
        description: "Calcule un score de confiance basé sur le nombre de matchs récents disponibles",
        impact: "Indique la fiabilité de l'analyse"
      },
      {
        title: "Forme par surface",
        description: "Filtre les résultats récents par surface quand possible",
        impact: "Prédictions plus spécialisées par surface"
      },
      {
        title: "Visualisation améliorée",
        description: "Affiche la séquence W/L des derniers matchs et les statistiques détaillées",
        impact: "Interface plus informative"
      }
    ],
    weights: {
      elo: "35% (réduit de 40%)",
      matchStats: "25% (inchangé)",
      recentForm: "20% (augmenté de 15%)",
      surfaceSpecialization: "10% (inchangé)",
      momentum: "10% (amélioré avec vraies données)"
    },
    dataQuality: {
      high: "Plus de 80% des matchs récents disponibles",
      medium: "50-80% des matchs récents disponibles", 
      low: "Moins de 50% des matchs récents disponibles"
    }
  };
} 